import { LINK_TYPE } from '../Hooks/NavigationMenu';

/**
 * Validation for navigation items and placements.
 *
 * Plain functions rather than a Yup schema: the rules are conditional on
 * `linkType` and on which destination field the operator chose, which reads
 * far worse as a chain of `.when()` clauses than as the branch it actually is.
 *
 * Reference: DPA-FE `docs/Controlled/MainMenu/03-model-and-mapping.md` §3-§4.
 */

/* -------------------------------------------------------------------------- */
/*  The action whitelist                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Behaviours a menu entry can trigger instead of navigating.
 *
 * ⚠ THE WEBSITE OWNS THIS LIST. The authority is
 * DPA-FE `src/app/config/menuActions.js`; this is a mirror so the CMS can offer
 * a dropdown instead of free text. Adding a key here without adding it there
 * produces an entry the website silently drops — which is the designed
 * behaviour for an unknown key, but not what the operator intended.
 *
 * Portal destinations are named by KEY, never by URL: the addresses live in
 * DPA-FE `src/app/config/portalLinks.js`, which is where EY Issue #3 put them
 * so the language decision is made in exactly one place.
 */
/**
 * `retired: true` means NO LONGER OFFERED, still RECOGNISED.
 *
 * The three portal actions were withdrawn from the picker on 2026-09-30 —
 * search, language and accessibility are the whole offer now. They are not
 * deleted, and that distinction is the whole point:
 *
 *   - `action:portal.verifyCertificate` is LIVE in placement 4 today
 *     («التحقق من الشهادة»). Deleting the key would make `isKnownAction`
 *     reject it, so that row would fail validation and could not be re-saved
 *     without the operator re-picking a destination it no longer offers.
 *   - DPA-FE's own whitelist (`src/app/config/menuActions.js`) still resolves
 *     all six, so existing rows keep working on the website either way. What
 *     changes here is only what can be CHOSEN.
 *
 * A retired action still renders in the select when it is the row's current
 * value, so an operator can see what the row does — see `menuActionOptions`.
 */
export const MENU_ACTIONS = Object.freeze([
  { key: 'action:search', label: 'فتح نافذة البحث' },
  { key: 'action:accessibility', label: 'خيارات إمكانية الوصول' },
  { key: 'action:language', label: 'تبديل اللغة' },
  {
    key: 'action:portal.studentLogin',
    label: 'بوابة الطلبة — تسجيل الدخول',
    retired: true,
  },
  {
    key: 'action:portal.verifyCertificate',
    label: 'بوابة الطلبة — التحقق من الشهادة',
    retired: true,
  },
  {
    key: 'action:portal.registerInterest',
    label: 'بوابة الطلبة — تسجيل الاهتمام',
    retired: true,
  },
]);

/**
 * What the picker shows: the offered actions, plus `current` when it names a
 * retired one so an existing row still displays its own value.
 */
export const menuActionOptions = (current) =>
  MENU_ACTIONS.filter((action) => !action.retired || action.key === current);

export const isActionKey = (value) =>
  typeof value === 'string' && value.startsWith('action:');

export const isKnownAction = (value) =>
  MENU_ACTIONS.some((action) => action.key === value);

/** `#section-3` — scroll to a home-page section. Bottom menu only. */
export const SECTION_PATTERN = /^#section-([1-9]\d*)$/;
export const isSectionAnchor = (value) =>
  typeof value === 'string' && SECTION_PATTERN.test(value);

/**
 * A row from the Pages catalogue whose `PageType` is `Section` — a region
 * INSIDE a page rather than a page of its own, authored as `/#news`,
 * `/about/overview#team` and so on.
 *
 * Distinct from `isSectionAnchor` above, and checked AFTER it: `#section-3`
 * also contains a hash but means something else entirely (scroll the home
 * page to its third region, resolved by the website from a fixed index).
 */
export const isPageSectionAnchor = (value) =>
  typeof value === 'string' &&
  value.includes('#') &&
  !isSectionAnchor(value) &&
  !isActionKey(value);

/** `/degree-programs/degree/{group}/{guid}` — a live programme page. */
export const DEGREE_PATH_PATTERN =
  /^\/degree-programs\/degree\/[a-z]+\/[0-9a-f-]{8,}$/i;
export const isDegreePath = (value) =>
  typeof value === 'string' && DEGREE_PATH_PATTERN.test(value.trim());

/** How the operator chose to express the destination. */
export const DESTINATION = Object.freeze({
  PAGE: 'page', // internalPageId, from the Pages catalogue (type=Page)
  PAGE_SECTION: 'pageSection', // a Pages catalogue row of type=Section
  // Formerly PATH («مسار داخلي»). Renamed with the product 2026-09-25: the
  // backend models it as a DYNAMIC PAGE — the item carries `linkType: 3` and
  // the URL in `dynamicUrl`. The value here is transient UI state, never
  // stored, so the rename costs nothing at the API boundary.
  DYNAMIC: 'dynamic',
  ACTION: 'action', // a whitelisted action: key
  SECTION: 'section', // #section-n
  EXTERNAL: 'external', // externalUrl
});

/**
 * Where a dynamic-page destination's URL came from — free text, or the
 * programme picker. UI state only: it is derived from the stored link on open and never
 * sent to the API, so a link typed by hand and the identical link chosen from
 * the picker are the same row.
 */
export const PATH_SOURCE = Object.freeze({
  MANUAL: 'manual',
  PROGRAMS: 'programs',
});

export const pathSourceOf = (linkOverride) =>
  isDegreePath(linkOverride) ? PATH_SOURCE.PROGRAMS : PATH_SOURCE.MANUAL;

/** Infer the destination kind of an existing placement, for edit mode. */
export const destinationOf = (placement) => {
  const item = placement?.item ?? {};
  const override = placement?.linkOverride ?? '';

  if (item.linkType === LINK_TYPE.EXTERNAL) return DESTINATION.EXTERNAL;
  // A deployed backend names the dynamic page outright (`linkType: 3`, or
  // the URL in `dynamicUrl`) — no sniffing needed. Rows saved before the
  // enum grew carry neither signal, only the URL in `linkOverride`; they
  // land on the final fallback below, which is the same destination.
  if (
    item.linkType === LINK_TYPE.DYNAMIC ||
    (typeof item.dynamicUrl === 'string' && item.dynamicUrl.trim())
  ) {
    return DESTINATION.DYNAMIC;
  }
  // A procedure names ITSELF on the item (`procedureName`), so re-opening one
  // for edit does not depend on the placement's `linkOverride` surviving the
  // round trip. `linkOverride` is still checked after it, for rows written
  // before the field existed.
  if (isActionKey(item.procedureName)) return DESTINATION.ACTION;
  if (isActionKey(override)) return DESTINATION.ACTION;
  // Order matters: `#section-3` is a scroll index, `/#news` is a catalogued
  // page section, and only the first has a fixed shape to match on.
  if (isSectionAnchor(override)) return DESTINATION.SECTION;
  // A catalogued page section now NAMES itself on the item. Checked before the
  // slug shape so the row survives a `linkOverride` that was never written or
  // did not round-trip; rows saved before this carried `linkType: 0` and fall
  // through to the shape test below, which is the same destination.
  // `#section-n` scrolls cannot collide here — they send `linkType: 4`.
  if (item.linkType === LINK_TYPE.SECTION) return DESTINATION.PAGE_SECTION;
  if (isPageSectionAnchor(override)) return DESTINATION.PAGE_SECTION;
  if (item.page?.id) return DESTINATION.PAGE;
  return DESTINATION.DYNAMIC;
};

/* -------------------------------------------------------------------------- */
/*  Field rules                                                                */
/* -------------------------------------------------------------------------- */

const ARABIC = /[؀-ۿ]/;

export const validateTitle = (value) => {
  const title = (value ?? '').trim();
  if (!title) return 'العنوان مطلوب.';
  if (title.length > 100) return 'العنوان يجب ألا يتجاوز 100 حرف.';
  return null;
};

/**
 * A warning, not an error — an operator may legitimately want a Latin label
 * such as "English" or a programme code.
 *
 * The site has ONE locale file (Arabic) and produces English through the Google
 * Translate overlay, so a title authored in English is shown to Arabic readers
 * untranslated. Worth saying; not worth blocking.
 */
export const warnOnLatinTitle = (value) => {
  const title = (value ?? '').trim();
  if (!title || ARABIC.test(title)) return null;
  return 'العنوان لا يحتوي على حروف عربية. الموقع عربي في الأصل ويترجم تلقائياً إلى الإنجليزية.';
};

/**
 * An internal path.
 *
 * Rejects protocol-relative (`//evil.com`) and scheme-bearing (`javascript:`)
 * values, which is why the check is not simply "starts with /".
 */
export const validateInternalPath = (value, { knownRoutes } = {}) => {
  const path = (value ?? '').trim();
  if (!path) return 'المسار مطلوب.';
  if (!path.startsWith('/')) return 'المسار يجب أن يبدأ بـ /';
  if (path.startsWith('//')) return 'المسار غير صالح.';

  const [pathname] = path.split('?');
  if (pathname.includes(':')) return 'المسار غير صالح.';

  if (Array.isArray(knownRoutes) && knownRoutes.length > 0) {
    const known = knownRoutes.some((route) => route === pathname);
    if (!known) {
      // A warning in error's clothing would be worse: the operator cannot tell
      // a genuinely new route from a typo, so this is surfaced separately.
      return null;
    }
  }
  return null;
};

/** Separate from the error so the UI can show it in amber, not red. */
export const warnOnUnknownPath = (value, knownRoutes) => {
  const path = (value ?? '').trim();
  if (!path.startsWith('/') || !Array.isArray(knownRoutes) || knownRoutes.length === 0) {
    return null;
  }
  const [pathname] = path.split('?');
  if (knownRoutes.includes(pathname)) return null;
  return 'هذا المسار غير معروف في الموقع. تأكد من صحته قبل الحفظ.';
};

export const validateExternalUrl = (value) => {
  const url = (value ?? '').trim();
  if (!url) return 'الرابط مطلوب.';
  if (!/^https:\/\//i.test(url)) return 'الرابط يجب أن يبدأ بـ https://';
  try {
    // eslint-disable-next-line no-new
    new URL(url);
  } catch (_error) {
    return 'الرابط غير صالح.';
  }
  return null;
};

export const validateAction = (value) => {
  if (!value) return 'اختر الإجراء.';
  if (!isKnownAction(value)) return 'هذا الإجراء غير معروف ولن يظهر في الموقع.';
  return null;
};

export const validateSection = (value) => {
  if (!value) return 'اختر رقم القسم.';
  if (!isSectionAnchor(value)) return 'رقم القسم غير صالح.';
  return null;
};

export const validatePageSection = (value) => {
  const slug = (value ?? '').trim();
  if (!slug) return 'اختر القسم.';
  if (!slug.includes('#')) return 'القسم المختار لا يحمل معرّف موضع (#).';
  if (slug.startsWith('//')) return 'العنوان غير صالح.';
  return null;
};

/**
 * An icon reference: a path the website serves, or an absolute https URL
 * (which is what the blob uploader returns).
 *
 * A warning rather than an error — the field was free text before this and
 * the live database may hold values this rule does not recognise. Blocking a
 * save on an icon would be a new way to fail an edit that used to work.
 */
export const warnOnIconUrl = (value) => {
  const url = (value ?? '').trim();
  if (!url) return null;
  if (/^https:\/\//i.test(url)) return null;
  if (url.startsWith('//')) return 'رابط الأيقونة غير صالح.';
  if (!url.startsWith('/'))
    return 'رابط الأيقونة يجب أن يبدأ بـ / أو https://';
  return null;
};

/* -------------------------------------------------------------------------- */
/*  Form-level                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * @returns {{ errors: Record<string,string>, warnings: Record<string,string> }}
 *   `errors` blocks the save; `warnings` are shown and can be ignored.
 */
export const validateMenuForm = (form, { knownRoutes } = {}) => {
  const errors = {};
  const warnings = {};

  const titleError = validateTitle(form.title);
  if (titleError) errors.title = titleError;
  else {
    const titleWarning = warnOnLatinTitle(form.title);
    if (titleWarning) warnings.title = titleWarning;
  }

  switch (form.destination) {
    case DESTINATION.PAGE:
      if (!form.internalPageId) errors.internalPageId = 'اختر الصفحة.';
      break;

    case DESTINATION.DYNAMIC: {
      const pathError = validateInternalPath(form.linkOverride, { knownRoutes });
      if (pathError) errors.linkOverride = pathError;
      else {
        const pathWarning = warnOnUnknownPath(form.linkOverride, knownRoutes);
        if (pathWarning) warnings.linkOverride = pathWarning;
      }
      break;
    }

    case DESTINATION.ACTION: {
      const actionError = validateAction(form.linkOverride);
      if (actionError) errors.linkOverride = actionError;
      break;
    }

    case DESTINATION.SECTION: {
      const sectionError = validateSection(form.linkOverride);
      if (sectionError) errors.linkOverride = sectionError;
      break;
    }

    case DESTINATION.PAGE_SECTION: {
      const pageSectionError = validatePageSection(form.linkOverride);
      if (pageSectionError) errors.linkOverride = pageSectionError;
      break;
    }

    case DESTINATION.EXTERNAL: {
      const urlError = validateExternalUrl(form.externalUrl);
      if (urlError) errors.externalUrl = urlError;
      break;
    }

    default:
      errors.destination = 'اختر نوع الرابط.';
  }

  // An internal destination opening in a new tab opens the site inside itself.
  // The live database already contains one of these; the form prevents more.
  if (form.destination !== DESTINATION.EXTERNAL && form.openInNewTab) {
    warnings.openInNewTab =
      'فتح رابط داخلي في نافذة جديدة يفتح الموقع داخل نفسه. يُنصح بإلغاء هذا الخيار.';
  }

  if (form.isShowButtonText && !form.isDisplayTypeButton) {
    errors.isShowButtonText = 'إظهار نص الزر متاح فقط عندما يكون العنصر زراً.';
  }

  // Two mutually exclusive renderings. Left unchecked, whichever the website
  // happens to test first silently wins, which is worse than being told here.
  if (form.isHeadTitle && form.isDisplayTypeButton) {
    errors.isHeadTitle = 'العنصر إمّا عنوان مجموعة أو زر — لا يمكن أن يكون الاثنين.';
  }

  const iconWarning = warnOnIconUrl(form.iconUrl);
  if (iconWarning) warnings.iconUrl = iconWarning;

  return { errors, warnings };
};

/**
 * Turn the form into the two API payloads.
 *
 * Deliberately explicit about nulls: the API distinguishes "not set" from
 * "empty string", and sending `""` for an absent id is a contract violation
 * (§6 — `null` or omit, never `""`).
 */
export const toApiPayloads = (
  form,
  { placementType, parentPlacementId, sequenceId, pageSectionId = null }
) => {
  const isExternal = form.destination === DESTINATION.EXTERNAL;
  const isDynamic = form.destination === DESTINATION.DYNAMIC;
  // A destination that names no page at all: a whitelisted `action:` key, or a
  // `#section-n` scroll on the bottom bar. Both carry their value in
  // `linkOverride` and have no `internalPageId` to give, so `linkType: 0`
  // (Internal) is rejected by the API — `InternalPageId is required when
  // LinkType is not External & DynamicUrl`. LINK_TYPE.ACTION (4) is the member
  // the deployed API exempts; see the enum's history note in
  // `Hooks/NavigationMenu.js`.
  const isAction =
    form.destination === DESTINATION.ACTION ||
    form.destination === DESTINATION.SECTION;
  // «قسم من الصفحة الرئيسية» names a REGION of a real page, and the enum has a
  // member for exactly that: `linkType: 2` (Section). It used to be sent as 0
  // (Internal) — the fall-through — which described it as an ordinary internal
  // page while supplying no `internalPageId`.
  //
  // It ships WITH an `internalPageId`: the enum note in
  // `Hooks/NavigationMenu.js` records that the API rejects any linkType but
  // External and Dynamic when no id is given — `InternalPageId is required
  // when LinkType is not External & DynamicUrl`. A `#section-n` scroll has no
  // page to name and so has to use ACTION (4); a catalogued page section DOES
  // have one, so it can satisfy the rule honestly. The id is resolved from the
  // sections catalogue by the caller — see `pageSectionId` below.
  const isPageSection = form.destination === DESTINATION.PAGE_SECTION;
  // The procedure's own key (`action:search`, …), carried ON THE ITEM so an
  // existing procedure is recognisable on read-back without inspecting the
  // placement. `null` — never `''` — for every other destination (§6).
  const procedureName =
    form.destination === DESTINATION.ACTION
      ? (form.linkOverride ?? '').trim() || null
      : null;
  // Every internal destination EXCEPT `PAGE` carries its value in
  // `linkOverride`. A catalogued page section keeps its SLUG there even now
  // that it also sends an id: the slug is what the public menu resolves from
  // today (`linkOverride ?? externalUrl ?? page.slug`), so dropping it would
  // blank every one of these links on the website. The id is additional, not
  // a replacement.
  const usesOverride =
    form.destination === DESTINATION.DYNAMIC ||
    form.destination === DESTINATION.ACTION ||
    form.destination === DESTINATION.SECTION ||
    form.destination === DESTINATION.PAGE_SECTION;

  const item = {
    title: (form.title ?? '').trim(),
    description: (form.description ?? '').trim() || null,
    // «صفحة ديناميكية» — agreed with the backend team 2026-09-25, ahead of
    // Swagger: `linkType: 3` (LINK_TYPE.DYNAMIC, the enum's next member
    // after Internal/External/Section) with the URL in a new `dynamicUrl`
    // field. Until the backend recognises 3, it may reject the save — a
    // visible, correct failure. `linkOverride` is STILL written on the
    // placement below: it is what the live public menu resolves from today,
    // and dropping it before the backend switches resolution would blank
    // every one of these links on the website. Explicit null otherwise, §6.
    linkType: isExternal
      ? LINK_TYPE.EXTERNAL
      : isDynamic
        ? LINK_TYPE.DYNAMIC
        : isAction
          ? LINK_TYPE.ACTION
          : isPageSection
            ? LINK_TYPE.SECTION
            : LINK_TYPE.INTERNAL,
    dynamicUrl: isDynamic ? (form.linkOverride ?? '').trim() : null,
    procedureName,
    // `PAGE` names its page directly from the dropdown; `PAGE_SECTION` names
    // the catalogue row the chosen slug belongs to, resolved by the caller.
    // Explicit null for every other destination (§6 — never `''`).
    internalPageId:
      form.destination === DESTINATION.PAGE
        ? form.internalPageId || null
        : isPageSection
          ? pageSectionId || null
          : null,
    externalUrl: isExternal ? (form.externalUrl ?? '').trim() : null,
    iconUrl: (form.iconUrl ?? '').trim() || null,
    openInNewTab: Boolean(form.openInNewTab),
    isActive: form.isActive !== false,
    isDisplayTypeButton: Boolean(form.isDisplayTypeButton),
    isShowButtonText: Boolean(form.isShowButtonText),
    // A group heading rather than a link — the website renders it in the
    // column-title face instead of as something to click. Purely presentational,
    // so it lives on the ITEM: a row used as a heading in one menu is a heading
    // everywhere it appears, which is the behaviour the item/placement split
    // already gives us for free.
    isHeadTitle: Boolean(form.isHeadTitle),
  };

  const placement = {
    placementType,
    parentPlacementId: parentPlacementId ?? null,
    linkOverride: usesOverride ? (form.linkOverride ?? '').trim() : null,
    isVisible: form.isVisible !== false,
    sequenceId: sequenceId ?? 0,
  };

  return { item, placement };
};
